import type en from '../en/settings'
import type { Translation } from '../types'

/** Settings */
const settings: Translation<typeof en> = {
  "settings.mail.sent": "E-mail de test envoyé à {to} via {host}. Votre propre serveur de messagerie est désormais utilisé.",
  "settings.mail.testFailed": "L’e-mail de test n’a pas pu être envoyé",
  "settings.mail.inUse": "Utilisé : votre propre serveur de messagerie.",
  "settings.mail.notYet": "Pas encore utilisé : envoyez un e-mail de test pour commencer à utiliser votre propre serveur de messagerie. D’ici là, les e-mails passent par le serveur de messagerie de la plateforme.",
  "settings.mail.sendTest": "Envoyer un e-mail de test",
  "settings.mail.testHint": "Part vers votre propre adresse, avec les paramètres enregistrés : enregistrez d’abord vos modifications.",
  "settings.saved": "Paramètres enregistrés",
  "settings.saveFailed": "Impossible d’enregistrer les paramètres",
  "settings.companySaved": "Informations de l’entreprise enregistrées",
  "settings.companySaveFailed": "Impossible d’enregistrer les informations de l’entreprise",
  "settings.licenseRenewed": "Licence renouvelée !",
  "settings.licenseInvalid": "Code de licence invalide ou déjà utilisé",
  "settings.title": "Paramètres de l’entreprise",
  "settings.save": "Enregistrer les paramètres",
  "settings.companyInfo": "Informations de l’entreprise",
  "settings.companyName": "Nom de l’entreprise",
  "settings.customDomain": "Domaine personnalisé",
  "settings.customDomainHint": "L’adresse à laquelle on se connecte sous votre propre marque. Une fois enregistrée, faites pointer son DNS vers cette plateforme et prouvez qu’elle vous appartient avec un enregistrement TXT (affiché ici). La plateforme s’occupe elle-même du certificat HTTPS.",
  "settings.saveCompany": "Enregistrer les informations",
  "settings.signIns": "Connexions",
  "settings.sessionDays": "Durée maximale de session (jours)",
  "settings.sessionDaysHelp": "Ensuite, tout le monde dans l’entreprise se reconnecte : de 1 jour à 6 mois (183 jours), 7 par défaut. Compté à partir de la connexion ; une modification s’applique aussi aux sessions en cours.",
  "settings.idleMax": "Déconnexion après une période d’inactivité : au maximum",
  "settings.idleHelp": "Une session inutilisée pendant cette durée est déconnectée (8 heures par défaut). Chacun peut choisir une durée plus courte dans Mon profil.",
  "settings.idleOffWarning": "Cela permet de désactiver l’une des protections des comptes de cette entreprise : une session laissée ouverte sur un ordinateur sans surveillance reste alors connectée jusqu’à sa durée maximale.",
  "settings.idleOffQuestion": "Autoriser à la désactiver ?",
  "settings.idleOffLabel": "Laisser chacun désactiver pour lui-même la déconnexion après inactivité",
  "settings.syncInterval": "Intervalle de synchronisation",
  "settings.dataPeriod": "Période de données par défaut",
  "settings.retention": "Conservation des données",
  "settings.automation": "Automatisation et alertes",
  "settings.autoApply": "Appliquer automatiquement les paramètres par défaut aux nouveaux sites",
  "settings.autoApplySub": "Appliquer automatiquement le modèle par défaut de l’entreprise lorsqu’un site se connecte",
  "settings.emailAlerts": "Alertes par e-mail",
  "settings.emailAlertsSub": "Envoyer des e-mails d’alerte aux administrateurs de l’entreprise lorsque des sites passent hors ligne ou que des alertes critiques se déclenchent",
  "settings.webhookAlerts": "Alertes webhook",
  "settings.webhookAlertsSub": "Envoyer les données d’alerte par POST aux URL de webhook configurées",
  "settings.smtp.title": "E-mail personnalisé / SMTP",
  "settings.smtp.intro": "Les alertes et les e-mails de configuration VPN passent par votre propre serveur de messagerie, depuis votre propre adresse. Les invitations et les réinitialisations de mot de passe viennent toujours de la plateforme, si bien qu’aucun serveur de messagerie extérieur ne voit jamais de lien de connexion. Laissez vide pour tout envoyer via la plateforme.",
  "settings.smtp.host": "Hôte SMTP",
  "settings.smtp.userPlaceholder": "vous@domaine.fr",
  "settings.smtp.keepPlaceholder": "(laisser vide pour conserver)",
  "settings.smtp.passwordPlaceholder": "Mot de passe SMTP",
  "settings.smtp.encrypted": "Chiffré en AES-256 — jamais renvoyé au navigateur",
  "settings.smtp.fromEmail": "Adresse de l’expéditeur",
  "settings.smtp.fromEmailPlaceholder": "bonjour@votreentreprise.fr",
  "settings.smtp.fromName": "Nom de l’expéditeur",
  "settings.overview": "Aperçu de l’entreprise",
  "settings.slug": "Identifiant",
  "settings.created": "Créée",
  "settings.brandingText": "Personnalisez l’apparence de votre plateforme : couleurs, logo, nom de l’application, polices et CSS personnalisé.",
  "settings.openBranding": "Ouvrir l’éditeur d’image de marque →",
  "settings.license": "Licence",
  "settings.expires": "Expire le",
  "settings.maxSites": "Sites max.",
  "settings.maxUsers": "Utilisateurs max.",
  "settings.renewLicense": "Renouveler la licence",
  "settings.licenseCode": "Code de licence",
  "settings.renew": "Renouveler",
  "settings.more": "Autres paramètres",
  "settings.link.branding": "Image de marque et apparence",
  "settings.link.vpn": "Configuration VPN",
  "settings.link.users": "Utilisateurs et rôles",
  "settings.dns.ok": "En place",
  "settings.dns.missing": "Manquant",
  "settings.dns.update": "À modifier",
  "settings.dns.unknown": "Vérification impossible",
  "settings.dns.title": "Enregistrements DNS pour les e-mails de {domain}",
  "settings.dns.freeMail": "{domain} appartient à un fournisseur de messagerie : personne d’autre que ce fournisseur ne peut y ajouter d’enregistrements, les destinataires risquent donc de traiter ces e-mails comme du spam. Utilisez une adresse de votre propre domaine.",
  "settings.dns.addRecords": "Ajoutez ces enregistrements TXT chez le fournisseur DNS de {domain}, afin que les serveurs de messagerie destinataires acceptent les e-mails au lieu de les marquer comme spam.",
  "settings.dns.copyName": "Copier le nom de l’enregistrement {type}",
  "settings.dns.copyValue": "Copier la valeur de l’enregistrement {type}",
  "settings.dns.now": "Actuel :",
  "settings.checkNow": "Vérifier maintenant",
  "settings.domain.proven": "Domaine prouvé",
  "settings.domain.serves": "{domain} affiche désormais votre tableau de bord.",
  "settings.domain.notFound": "Pas encore trouvé",
  "settings.domain.dnsSlow": "Les modifications DNS peuvent mettre du temps à apparaître — réessayez dans quelques minutes.",
  "settings.domain.checkFailed": "Échec de la vérification",
  "settings.domain.ownershipProven": "Propriété prouvée",
  "settings.domain.ownershipNotYet": "Propriété pas encore prouvée",
  "settings.domain.dnsHere": "Le DNS pointe ici",
  "settings.domain.dnsNotYet": "Le DNS ne pointe pas encore ici",
  "settings.domain.addTxt": "Ajoutez cet enregistrement TXT chez votre fournisseur DNS. Tant qu’il n’est pas trouvé, {domain} affiche une page « en cours de configuration »",
  "settings.domain.releaseBefore": " ; s’il n’est pas trouvé d’ici le ",
  "settings.domain.releaseAfter": ", le domaine est libéré de votre entreprise",
  "settings.domain.copyName": "Copier le nom de l’enregistrement",
  "settings.domain.copyValue": "Copier la valeur de l’enregistrement",
  "settings.checked": " · vérifié {when}",
  "settings.domain.pointA": "Faites aussi pointer un enregistrement A pour {domain} vers {ip}.",
  "settings.domain.thisServer": "ce serveur",
  "settings.sso.saved": "Authentification unique enregistrée",
  "settings.sso.intro": "Les utilisateurs se connectent avec votre propre fournisseur d’identité (OpenID Connect : Microsoft Entra ID, Google Workspace, Okta, Keycloak et d’autres). Son authentification multifacteur remplace la nôtre. Enregistrez ces adresses de retour de connexion chez le fournisseur :",
  "settings.sso.copy": "Copier {url}",
  "settings.sso.issuer": "Émetteur",
  "settings.sso.clientId": "ID client",
  "settings.sso.clientSecret": "Secret client",
  "settings.sso.secretStored": "Enregistré — saisissez-en un nouveau pour le remplacer",
  "settings.sso.on": "L’authentification unique est activée",
  "settings.sso.autoJoin": "Les personnes des domaines prouvés rejoignent l’entreprise lors de leur première connexion",
  "settings.sso.withRole": "Avec le rôle",
  "settings.sso.noRole": "Aucun rôle (seulement ce que tout le monde peut voir)",
  "settings.sso.require": "Exiger l’authentification unique : pas de connexion par mot de passe pour les personnes des domaines prouvés",
  "settings.sso.breakGlassNote": "Les administrateurs de secours (ci-dessous) conservent leur mot de passe et leur connexion en deux étapes, pour le cas où le fournisseur d’identité serait injoignable.",
  "settings.sso.save": "Enregistrer l’authentification unique",
  "settings.sso.domains": "Domaines",
  "settings.sso.domainsIntro": "Prouvez un domaine avec un enregistrement TXT : ses adresses se connectent alors (et s’associent) sans mot de passe",
  "settings.sso.domainsUntil": " — tant qu’aucun n’est prouvé, seules les personnes qui ont associé leur compte depuis leur profil peuvent utiliser l’authentification unique",
  "settings.sso.proven": "Prouvé",
  "settings.sso.notProven": "Pas encore prouvé",
  "settings.sso.removeConfirm": "Supprimer {domain} ? Ses utilisateurs ne pourront alors se connecter avec l’authentification unique qu’après l’avoir associée eux-mêmes.",
  "settings.sso.domainRemoved": "Domaine supprimé",
  "settings.sso.copyName": "Copier le nom de l’enregistrement pour {domain}",
  "settings.sso.copyValue": "Copier la valeur de l’enregistrement pour {domain}",
  "settings.sso.domainAdded": "Domaine ajouté",
  "settings.sso.domainToAdd": "Domaine à ajouter",
  "settings.sso.addDomain": "Ajouter un domaine",
  "settings.sso.breakGlass": "Administrateurs de secours",
  "settings.apikeys.revoked": "Clé API révoquée",
  "settings.apikeys.revokeFailed": "La clé n’a pas pu être révoquée",
  "settings.apikeys.copied": "Copié dans le presse-papiers",
  "settings.apikeys.title": "Clés API",
  "settings.apikeys.subtitle": "Accès à votre compte pour des scripts et d’autres systèmes",
  "settings.apikeys.create": "Créer une clé API",
  "settings.apikeys.createdTitle": "Clé API créée — copiez-la maintenant",
  "settings.apikeys.createdText": "La clé complète n’est affichée qu’une seule fois. Conservez-la en lieu sûr.",
  "settings.apikeys.hide": "Masquer la clé",
  "settings.apikeys.show": "Afficher la clé",
  "settings.apikeys.dismiss": "Je l’ai conservée — fermer",
  "settings.apikeys.prefix": "Préfixe",
  "settings.apikeys.permissions": "Droits",
  "settings.apikeys.lastUsed": "Dernière utilisation",
  "settings.apikeys.expires": "Expire",
  "settings.apikeys.created": "Créée",
  "settings.apikeys.empty": "Aucune clé API pour l’instant",
  "settings.apikeys.revokedBadge": "Révoquée",
  "settings.apikeys.none": "Aucun",
  "settings.apikeys.never": "Jamais",
  "settings.apikeys.revokeConfirm": "Révoquer « {name} » ? Cette action est irréversible.",
  "settings.apikeys.revoke": "Révoquer la clé",
  "settings.apikeys.revokeNamed": "Révoquer {name}",
  "settings.apikeys.nameRequired": "Un nom est obligatoire",
  "settings.apikeys.createFailed": "La clé n’a pas pu être créée",
  "settings.apikeys.keyName": "Nom de la clé *",
  "settings.apikeys.keyNamePlaceholder": "p. ex. pipeline CI/CD, script de supervision",
  "settings.apikeys.days30": "30 jours",
  "settings.apikeys.days90": "90 jours",
  "settings.apikeys.year1": "1 an",
  "settings.apikeys.permHint": "Choisissez les droits de cette clé. Une clé en nécessite au moins un.",
  "settings.apikeys.createKey": "Créer la clé",
  "settings.branding.resetDone": "Identité visuelle rétablie par défaut",
  "settings.branding.resetFailed": "Échec de la réinitialisation",
  "settings.branding.saved": "Identité visuelle enregistrée",
  "settings.branding.saveFailed": "L’identité visuelle n’a pas pu être enregistrée",
  "settings.branding.title": "Identité visuelle",
  "settings.branding.subtitle": "L’apparence du tableau de bord pour votre équipe",
  "settings.branding.resetConfirm": "Rétablir toute l’identité visuelle par défaut de Velanox ? Toutes les couleurs, polices et logos personnalisés seront supprimés.",
  "settings.branding.reset": "Rétablir par défaut",
  "settings.branding.save": "Enregistrer l’identité visuelle",
  "settings.branding.identity": "Identité",
  "settings.branding.appName": "Nom de l’application",
  "settings.branding.logoUrl": "URL du logo",
  "settings.branding.faviconUrl": "URL du favicon",
  "settings.branding.loginBanner": "Image de bannière de connexion",
  "settings.branding.colors": "Couleurs",
  "settings.branding.color.primaryColor": "Principale",
  "settings.branding.color.secondaryColor": "Secondaire",
  "settings.branding.color.accentColor": "Accent",
  "settings.branding.color.backgroundColor": "Arrière-plan",
  "settings.branding.color.sidebarColor": "Barre latérale",
  "settings.branding.color.textColor": "Texte",
  "settings.branding.colourOf": "Couleur {name}",
  "settings.branding.colourCode": "Code couleur {name}",
  "settings.branding.themeDefault": "par défaut du thème",
  "settings.branding.typography": "Typographie et forme",
  "settings.branding.font": "Police",
  "settings.branding.fontDefault": "Par défaut de la plateforme (Geist)",
  "settings.branding.radius": "Arrondi des angles",
  "settings.branding.radiusDefault": "Par défaut de la plateforme",
  "settings.branding.radius.0": "Net (0px)",
  "settings.branding.radius.4": "Léger (4px)",
  "settings.branding.radius.8": "Doux (8px)",
  "settings.branding.radius.12": "Arrondi (12px)",
  "settings.branding.radius.16": "Très arrondi (16px)",
  "settings.branding.customCss": "CSS personnalisé",
  "settings.branding.customCssHint": "Avancé : CSS ajouté à chaque page. 50 000 caractères au maximum.",
  "settings.branding.cssPlaceholder": "/* CSS personnalisé */",
  "settings.branding.preview": "Aperçu en direct",
  "settings.branding.applyPreview": "Appliquer l’aperçu",
  "settings.branding.sampleBadge": "Badge d’exemple",
  "settings.branding.previewHint": "Choisissez « Appliquer l’aperçu » pour voir les modifications en direct dans la barre latérale",
  "settings.messages.part.emailSubject": "Objet de l’e-mail",
  "settings.messages.part.emailBody": "Texte de l’e-mail",
  "settings.messages.part.whatsappBody": "Texte WhatsApp",
  "settings.messages.part.rcsBody": "Texte RCS",
  "settings.messages.wa.approved": "Approuvé par WhatsApp : votre texte est envoyé",
  "settings.messages.wa.rejected": "Refusé par WhatsApp. Le texte par défaut est envoyé.",
  "settings.messages.wa.rejectedWhy": "Refusé par WhatsApp : {reason}. Le texte par défaut est envoyé.",
  "settings.messages.wa.failed": "Pas encore soumis ({reason}). Nouvel essai toutes les 30 minutes ; d’ici là, le texte par défaut est envoyé.",
  "settings.messages.wa.failedPlain": "Pas encore soumis. Nouvel essai toutes les 30 minutes ; d’ici là, le texte par défaut est envoyé.",
  "settings.messages.wa.paused": "Mis en pause par WhatsApp. Le texte par défaut est envoyé.",
  "settings.messages.wa.disabled": "Désactivé par WhatsApp. Le texte par défaut est envoyé.",
  "settings.messages.wa.pending": "En attente de l’approbation de WhatsApp (généralement sous un jour). D’ici là, le texte par défaut est envoyé.",
  "settings.messages.notChanged": "Non modifié",
  "settings.messages.notSaved": "Non enregistré",
  "settings.messages.tryAgain": "Réessayez dans un instant.",
  "settings.messages.title": "Messages",
  "settings.messages.subtitle": "Ce que vos collaborateurs reçoivent par {channels}. Modifiez le texte ou désactivez un message.",
  "settings.messages.channel.email": "e-mail",
  "settings.messages.channels2": "{a} et {b}",
  "settings.messages.channels3": "{a}, {b} et {c}",
  "settings.messages.defaultsPending": "Votre propre compte WhatsApp n’a pas encore approuvé les textes standard : d’ici là, les messages WhatsApp partent en texte simple, qui n’arrive que dans les 24 heures suivant le dernier message du destinataire.",
  "settings.messages.variablesInfo": "Les mots entre doubles accolades, comme {example}, sont remplis à l’envoi du message. Les liens proviennent uniquement des variables de lien. Les invitations et réinitialisations de mot de passe concernent la sécurité : elles sont donc toujours envoyées.",
  "settings.messages.noLicence": "Votre licence n’inclut pas la modification des messages. Vous pouvez toutefois rétablir un message par défaut.",
  "settings.messages.sentEmail": "Envoyé par e-mail",
  "settings.messages.sentWhatsapp": "Envoyé par WhatsApp",
  "settings.messages.sentRcs": "Envoyé par RCS",
  "settings.messages.ownWording": "Texte personnalisé",
  "settings.messages.alwaysSent": "Toujours envoyé",
  "settings.messages.on": "Activé",
  "settings.messages.off": "Désactivé",
  "settings.messages.sendNamed": "Envoyer « {label} »",
  "settings.messages.resetConfirm": "Rétablir le texte par défaut de « {label} » ?",
  "settings.messages.resetDone": "Texte par défaut rétabli",
  "settings.messages.ownMeta": "Votre propre WhatsApp passe par Meta : ses textes sont les modèles indiqués dans Paramètres de l’entreprise → WhatsApp.",
  "settings.messages.twilioOnly": "Les textes WhatsApp peuvent être modifiés lorsque la plateforme envoie WhatsApp via Twilio.",
  "settings.messages.waRules": "Un seul paragraphe, des mots avant la première variable et après la dernière, jamais deux variables côte à côte. Un texte modifié doit être approuvé par WhatsApp avant d’être utilisé.",
  "settings.messages.rcsNote": "Arrive dans l’application de messages du téléphone. Utilisé immédiatement : les textes RCS ne nécessitent aucune approbation.",
  "settings.messages.insertVariable": "Insérer une variable",
  "settings.messages.example": "Exemple : {value}",
  "settings.messages.required": "* doit rester dans le texte de l’e-mail",
  "settings.messages.save": "Enregistrer",
  "settings.messages.backToDefault": "Rétablir par défaut",
  "settings.messages.previewRegion": "Aperçu avec des valeurs d’exemple",
  "settings.messages.preview": "Aperçu",
  "settings.vpn.downloadFailed": "Échec du téléchargement",
  "settings.vpn.mayBeCreating": "Le fichier VPN est peut-être encore en cours de création.",
  "settings.vpn.creating": "Compte VPN demandé",
  "settings.vpn.creatingText": "Votre compte VPN est en cours de création. Actualisez dans 30 secondes.",
  "settings.vpn.createFailed": "Le compte VPN n’a pas pu être créé",
  "settings.vpn.contactAdmin": "Contactez votre administrateur.",
  "settings.vpn.regenerated": "Nouveau fichier VPN créé",
  "settings.vpn.regeneratedText": "Téléchargez votre nouveau fichier ci-dessous.",
  "settings.vpn.regenerateFailed": "Aucun nouveau fichier VPN n’a pu être créé",
  "settings.vpn.memberConfirm": "Créer un nouveau fichier VPN pour « {name} » ? L’actuel cessera de fonctionner.",
  "settings.vpn.memberDone": "Compte VPN demandé",
  "settings.vpn.memberDoneText": "Un nouveau fichier VPN est en cours de création pour {name}.",
  "settings.vpn.suspendConfirm": "Suspendre le VPN de « {name} » ?",
  "settings.vpn.enableConfirm": "Réactiver le VPN de « {name} » ?",
  "settings.vpn.suspended": "VPN suspendu",
  "settings.vpn.enabled": "VPN réactivé",
  "settings.vpn.notChanged": "Non modifié",
  "settings.vpn.siteRegenerated": "Nouveau fichier VPN créé pour le site",
  "settings.vpn.subtitle": "OpenVPN : un réseau distinct pour chaque entreprise",
  "settings.vpn.mine": "Mon compte VPN",
  "settings.vpn.lastSeen": "Vu pour la dernière fois",
  "settings.vpn.download": "Télécharger le .ovpn",
  "settings.vpn.regenerateMineConfirm": "Créer un nouveau fichier VPN ? Votre fichier .ovpn actuel cessera de fonctionner.",
  "settings.vpn.regenerateHint": "Nouveau fichier : si l’actuel est perdu ou a pu être vu par d’autres",
  "settings.vpn.regenerate": "Renouveler le certificat",
  "settings.vpn.suspendedMine": "Votre compte VPN est suspendu. Contactez votre administrateur.",
  "settings.vpn.noAccount": "Pas encore de compte VPN.",
  "settings.vpn.createMine": "Créer mon compte VPN",
  "settings.vpn.network": "Réseau VPN de l’entreprise",
  "settings.vpn.usersNet": "Réseau des utilisateurs",
  "settings.vpn.sitesNet": "Réseau des sites",
  "settings.vpn.isolation": "Les utilisateurs peuvent atteindre chaque site de l’entreprise. Les sites ne peuvent pas s’atteindre entre eux.",
  "settings.vpn.networkPending": "Le réseau VPN est en cours de configuration…",
  "settings.vpn.all": "Tous les comptes VPN",
  "settings.vpn.count": { one: "{count} compte", other: "{count} comptes" },
  "settings.vpn.account": "Compte",
  "settings.vpn.type": "Type",
  "settings.vpn.type.USER": "Personne",
  "settings.vpn.type.SITE": "Site",
  "settings.vpn.empty": "Aucun compte VPN pour l’instant",
  "settings.vpn.downloadSite": "Télécharger le fichier VPN du site",
  "settings.vpn.siteConfirm": "Créer un nouveau fichier VPN pour « {name} » ? L’actuel cessera de fonctionner.",
  "settings.vpn.suspend": "Suspendre le VPN",
  "settings.vpn.enable": "Réactiver le VPN",
  "settings.vpn.createFor": "Créer le compte VPN",
  "settings.vpn.setup": "Instructions d’installation",
  "settings.vpn.tab.personal": "Appareil personnel",
  "settings.vpn.tab.unifi": "UniFi OS (routeur du site)",
  "settings.vpn.pc.1": "Téléchargez OpenVPN Connect sur https://openvpn.net/client/",
  "settings.vpn.pc.2": "Choisissez + puis « Upload File »",
  "settings.vpn.pc.3": "Sélectionnez votre fichier .ovpn",
  "settings.vpn.pc.4": "Choisissez Connect — aucun nom d’utilisateur ni mot de passe requis",
  "settings.vpn.pc.note": "OpenVPN Connect 3.x est requis. L’ancien OpenVPN GUI fonctionne aussi.",
  "settings.vpn.linux.1": "Installation : sudo apt install openvpn (ou l’équivalent pour votre distribution)",
  "settings.vpn.linux.2": "Connexion : sudo openvpn --config votre-nom.ovpn",
  "settings.vpn.linux.3": "Ou utilisez NetworkManager : importez le fichier .ovpn via le menu réseau",
  "settings.vpn.ios.1": "Installez « OpenVPN Connect » depuis l’App Store",
  "settings.vpn.ios.2": "Envoyez le fichier .ovpn sur votre appareil par AirDrop ou e-mail",
  "settings.vpn.ios.3": "Choisissez « Ouvrir avec OpenVPN Connect » lorsque c’est demandé",
  "settings.vpn.ios.4": "Choisissez Add puis Connect",
  "settings.vpn.android.1": "Installez « OpenVPN Connect » depuis le Play Store",
  "settings.vpn.android.2": "Partagez le fichier .ovpn avec OpenVPN Connect",
  "settings.vpn.android.3": "Choisissez Import puis Connect",
  "settings.vpn.security": "Sécurité :",
  "settings.vpn.securityText": "Gardez votre fichier .ovpn privé : c’est votre accès personnel. S’il est perdu ou a pu être vu par d’autres, créez un nouveau fichier ci-dessus ; l’ancien cessera de fonctionner.",
  "settings.webhooks.event.site.offline": "Site hors ligne",
  "settings.webhooks.event.site.online": "Site de nouveau en ligne",
  "settings.webhooks.event.alert.critical": "Alertes critiques",
  "settings.webhooks.event.alert.error": "Alertes d’erreur",
  "settings.webhooks.event.alert.warning": "Avertissements",
  "settings.webhooks.event.alert.info": "Informations",
  "settings.webhooks.event.license.expiring": "Licence bientôt expirée",
  "settings.webhooks.deleted": "Webhook supprimé",
  "settings.webhooks.deleteFailed": "Le webhook n’a pas pu être supprimé",
  "settings.webhooks.toggleFailed": "Le webhook n’a pas pu être modifié",
  "settings.webhooks.testDelivered": "Test livré",
  "settings.webhooks.answered": "Votre point de terminaison a répondu {code}.",
  "settings.webhooks.testFailed": "Échec du test",
  "settings.webhooks.noResponse": "Aucune réponse",
  "settings.webhooks.unreachable": "Vérifiez que l’URL du webhook est joignable et répond avec un statut 2xx.",
  "settings.webhooks.title": "Webhooks",
  "settings.webhooks.subtitle": "Événements envoyés à vos propres systèmes dès qu’ils se produisent, par HTTP POST",
  "settings.webhooks.add": "Ajouter un webhook",
  "settings.webhooks.eventsLabel": "Événements :",
  "settings.webhooks.info": "Chaque webhook reçoit les événements choisis pour lui (tous si aucun n’est choisi). Les données sont signées en HMAC-SHA256 avec le secret de votre webhook.",
  "settings.webhooks.events": "Événements",
  "settings.webhooks.updated": "Dernière mise à jour",
  "settings.webhooks.empty": "Aucun webhook pour l’instant",
  "settings.webhooks.allEvents": "Tous les événements",
  "settings.webhooks.disable": "Désactiver",
  "settings.webhooks.enable": "Activer",
  "settings.webhooks.sendTest": "Envoyer un événement de test",
  "settings.webhooks.edit": "Modifier",
  "settings.webhooks.deleteConfirm": "Supprimer le webhook « {name} » ?",
  "settings.webhooks.nameRequired": "Un nom est obligatoire",
  "settings.webhooks.urlRequired": "Une URL est obligatoire",
  "settings.webhooks.saveFailed": "Le webhook n’a pas pu être enregistré",
  "settings.webhooks.editTitle": "Modifier le webhook",
  "settings.webhooks.name": "Nom *",
  "settings.webhooks.namePlaceholder": "Mon webhook",
  "settings.webhooks.secret": "Secret",
  "settings.webhooks.secretKeep": "(laissez vide pour conserver l’actuel)",
  "settings.webhooks.secretRecommended": "(recommandé)",
  "settings.webhooks.secretPlaceholder": "Secret facultatif pour la signature HMAC",
  "settings.webhooks.signed": "S’il est défini, chaque envoi est signé dans cet en-tête :",
  "settings.webhooks.noneChosen": "(aucun choisi : tous)",
  "settings.webhooks.saveChanges": "Enregistrer les modifications",
  "settings.messages.who": "Qui reçoit ce texte",
  "settings.messages.who.everyone": "Tout le monde, quelle que soit sa langue",
  "settings.messages.who.perLanguage": "Par langue — les autres reçoivent le texte par défaut dans leur langue",
  "settings.messages.language": "Langue",
  "settings.messages.hasOwnText": "a son propre texte",
  "settings.messages.everyoneHint": "Écrit une fois, envoyé tel quel à tout le monde. Les parties que vous ne modifiez pas suivent la langue de chacun.",
  "settings.messages.languageHint": "Les personnes qui lisent le {language} reçoivent ce texte ; le texte par défaut en {language} s’affiche tant que vous ne le modifiez pas.",
  "settings.messages.removeLanguage": "Supprimer le texte en {language}",
  "settings.messages.removeLanguageConfirm": "Supprimer le texte en {language} de « {label} » ? Les personnes qui lisent le {language} recevront le texte par défaut.",
  "settings.messages.removed": "Texte supprimé",
  "settings.tabs": "Sections des paramètres",
  "settings.tab.general": "Général",
  "settings.tab.security": "Sécurité",
  "settings.tab.monitoring": "Surveillance et alertes",
  "settings.tab.messaging": "Messagerie",
  "settings.tab.licence": "Licence",
  "settings.unsaved": "modifications non enregistrées",
  "settings.languageTime": "Langue et fuseau horaire",
  "settings.messaging.none": "Votre licence n’inclut ni serveur de messagerie propre, ni WhatsApp, ni RCS : les messages passent par la plateforme.",
  "settings.logo.title": "Logo",
  "settings.logo.alt": "Logo de {name}",
  "settings.logo.hint": "Représente l’entreprise là où vous la choisissez après la connexion, et dans les listes. Toute image (PNG, JPEG, WebP, GIF, SVG, AVIF…), jusqu’à 10 Mo ; elle est redimensionnée en 256 × 256.",
  "settings.logo.choose": "Choisir une image",
  "settings.logo.change": "Changer d’image",
  "settings.logo.remove": "Supprimer le logo",
  "settings.logo.removeConfirm": "Supprimer le logo de {name} ?",
  "settings.logo.saved": "Logo enregistré",
  "settings.logo.removed": "Logo supprimé",
  "settings.logo.notSaved": "Logo non enregistré",
  "settings.logo.wrongType": "Choisissez un fichier image",
  "settings.logo.tooLarge": "L’image dépasse 10 Mo",
  "settings.logo.unreadable": "Ce navigateur ne peut pas ouvrir cette image : enregistrez-la en PNG ou JPEG et réessayez",
  "settings.banner.title": "Logo bannière",
  "settings.banner.hint": "Affiché au milieu de la barre du haut à la place du nom de l’entreprise. Une image large, de tout type (PNG, JPEG, WebP, GIF, SVG, AVIF…), jusqu’à 10 Mo.",
  "settings.banner.remove": "Supprimer le logo bannière",
  "settings.banner.removeConfirm": "Supprimer le logo bannière de {name} ?",
  "settings.banner.saved": "Logo bannière enregistré",
  "settings.banner.removed": "Logo bannière supprimé",
  "settings.banner.notSaved": "Logo bannière non enregistré",
  "settings.tab.rental": "Location",
  "settings.tab.connections": "Connexions",
  "settings.connections.intro": "Les contrôleurs par lesquels vos sites sont joints. Chacun est vérifié à l’enregistrement ; un secret n’est jamais réaffiché.",
  "settings.connections.none": "Aucune connexion pour l’instant : ajoutez-en une depuis la page Sites.",
  "settings.connections.saved": "Connexion enregistrée",
  "settings.connections.saveFailed": "Impossible d’enregistrer la connexion",
  "settings.connections.deleted": "Connexion supprimée",
  "settings.connections.deleteFailed": "Impossible de supprimer la connexion",
  "settings.connections.sites": { one: "{count} site", other: "{count} sites" },
  "settings.connections.checked": "vérifiée {when}",
  "settings.connections.waiting": "En attente de la connexion VPN du contrôleur : téléchargez son .ovpn et lancez-le sur l’ordinateur du contrôleur.",
  "settings.connections.save": "Enregistrer",
  "settings.connections.addSites": "Ajouter des sites",
  "settings.connections.edit": "Modifier",
  "settings.connections.deleteConfirm": "Supprimer la connexion {name} ?",
  "settings.connections.newCert": "Nouveau certificat",
  "settings.connections.newCertDone": "Nouveau certificat émis : téléchargez le .ovpn et lancez-le sur l’ordinateur du contrôleur",
  "settings.connections.newCertFailed": "Impossible d’émettre un nouveau certificat",
  "settings.phonesStay": "Les téléphones peuvent rester connectés (l’application mobile, ouverte avec Face ID ou une empreinte)",
  "settings.phonesStayHelp": "La limite d’inactivité ci-dessus compte aussi le temps dans l’application ; ensuite, le téléphone redemande Face ID.",
  "settings.phonesOffConfirm": "Désactiver ? À l’enregistrement, chaque téléphone de l’entreprise est déconnecté et redemandera le mot de passe.",
  "settings.messages.part.pushTitle": "Titre de la notification push",
  "settings.messages.part.pushBody": "Texte de la notification push",
  "settings.messages.sentPush": "Envoyé par notification push (application mobile)",
  "settings.messages.pushNote": "Affiché sur l'écran verrouillé du téléphone sous le nom de l'application Velanox ; 100 et 500 caractères au plus.",
  "settings.messages.pushPreview": "Application Velanox · notification push",
  "settings.simPins.title": "Cartes SIM",
  "settings.simPins.help": "Le code PIN de l'entreprise pour les cartes SIM des routeurs Teltonika, et les cartes SIM ayant leur propre code PIN. Les codes PIN s'affichent sous forme de points ; Afficher demande votre mot de passe et est journalisé.",
  "settings.simPins.companyPin": "Code PIN de l'entreprise",
  "settings.simPins.notSet": "Non défini",
  "settings.simPins.set": "Définir",
  "settings.simPins.save": "Enregistrer",
  "settings.simPins.newCompanyPin": "Nouveau code PIN de l'entreprise (4 à 8 chiffres)",
  "settings.simPins.changeHelp": "Les cartes SIM déjà verrouillées avec le code PIN actuel de l'entreprise le gardent comme code PIN propre.",
  "settings.simPins.show": "Afficher",
  "settings.simPins.ownPins": "Cartes SIM avec leur propre code PIN",
  "settings.simPins.noOwn": "Aucune pour l'instant.",
  "settings.simPins.removeConfirm": "Oublier le code PIN de la carte SIM {iccid} ?",
  "settings.simPins.notShown": "Le code PIN n'a pas été affiché",
  "settings.simPins.notSaved": "Non enregistré",
  "settings.simPins.removeNoteConfirm": "Oublier la note de la carte SIM {iccid} ? Elle reste sur le code PIN de l'entreprise.",
  "settings.pushFirst.title": "Push d’abord",
  "settings.pushFirst.companyIntro": "S’applique au WhatsApp et au RCS propres à cette entreprise.",
  "settings.pushFirst.on": "Le push part d’abord",
  "settings.pushFirst.onHelp": "Une alerte part en push vers ceux qui ont l’application ; WhatsApp ou RCS ne suivent que si elle n’a pas été vue pendant l’attente.",
  "settings.pushFirst.wait": "Attente avant WhatsApp ou RCS (minutes)",
  "settings.pushFirst.waitRange": "De 1 à 60 minutes entières",
  "settings.pushFirst.anyone": "Ne pas envoyer WhatsApp ni RCS dès que quelqu’un l’a vu",
  "settings.pushFirst.anyoneHelp": "Un collègue qui ouvre le site compte pour tous ceux qui en ont été avertis.",
  "settings.pushFirst.save": "Enregistrer",
  "settings.backdrop.dark": "Fond en mode sombre",
  "settings.backdrop.light": "Fond en mode clair",
  "settings.backdrop.none": "Aucun",
  "settings.backdrop.default.dark": "Blanc chaud",
  "settings.backdrop.default.light": "Sombre du tableau de bord",
  "settings.backdrop.own": "Couleur personnalisée",
  "settings.backdrop.colour.dark": "Couleur du fond en mode sombre",
  "settings.backdrop.colour.light": "Couleur du fond en mode clair",
  "settings.backdrop.saved": "Fond enregistré",
  "settings.backdrop.notSaved": "Fond non enregistré",
  "settings.backdrop.help": "Pour une image qui disparaît dans un des modes : un fond clair derrière des lettres sombres, un fond sombre derrière des lettres claires.",
}

export default settings
